import React, { useState, useEffect } from "react";
import { User, Mail, Phone, Loader2, CheckCircle, XCircle } from "lucide-react";

export default function UserProfile() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchUserDetails();
  }, []);

  const fetchUserDetails = async () => {
    try {
      const token = localStorage.getItem('user');
      const response = await fetch('https://api.samasong.com/sama-music/users/get', {
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : ''
        }
      });
      const data = await response.json();
      if (data.success) {
        setUser(data.data);
      }
    } catch (err) {
      console.error('Error fetching user:', err);
      setError('Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-pink-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-950 text-white p-8">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">User Profile</h1>
        
        {error && (
          <div className="bg-red-500/20 border border-red-500/50 p-4 rounded-lg mb-4">
            {error}
          </div>
        )}

        <div className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10">
          <div className="flex items-center gap-6 mb-8">
            <img
              src={user?.avatar || "https://i.pravatar.cc/150"}
              alt="avatar"
              className="w-24 h-24 rounded-full border-4 border-pink-500"
            />
            <div>
              <h2 className="text-2xl font-semibold">{user?.name || "User"}</h2>
              <p className="text-gray-400">{user?.email || "No email"}</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-4 p-4 bg-white/5 rounded-lg">
              <Phone className="w-5 h-5 text-pink-400" />
              <div className="flex-1">
                <p className="text-gray-400 text-sm">Mobile</p>
                <p className="text-lg">{user?.mobile || "Not provided"}</p>
              </div>
              {user?.isMobileVerified ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : (
                <XCircle className="w-5 h-5 text-red-500" />
              )}
            </div>

            <div className="flex items-center gap-4 p-4 bg-white/5 rounded-lg">
              <Mail className="w-5 h-5 text-pink-400" />
              <div className="flex-1">
                <p className="text-gray-400 text-sm">Email</p>
                <p className="text-lg">{user?.email || "Not provided"}</p>
              </div>
              {user?.isEmailVerified ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : (
                <XCircle className="w-5 h-5 text-red-500" />
              )}
            </div>

            <div className="flex items-center gap-4 p-4 bg-white/5 rounded-lg">
              <User className="w-5 h-5 text-pink-400" />
              <div>
                <p className="text-gray-400 text-sm">Name</p>
                <p className="text-lg">{user?.name || "Not provided"}</p>
              </div>
            </div>

            <div className="flex items-center gap-4 p-4 bg-white/5 rounded-lg">
              <div className="w-5 h-5 flex items-center justify-center">
                <span className="text-pink-400 text-lg">🎵</span>
              </div>
              <div className="flex-1">
                <p className="text-gray-400 text-sm">Subscription</p>
                <p className="text-lg">{user?.isSubscribed ? "Premium" : "Free"}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm ${user?.isSubscribed ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                {user?.isSubscribed ? 'Active' : 'Inactive'}
              </span>
            </div>

            <div className="flex items-center gap-4 p-4 bg-white/5 rounded-lg">
              <div className="w-5 h-5 flex items-center justify-center">
                <span className="text-pink-400 text-lg">👤</span>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Role</p>
                <p className="text-lg capitalize">{user?.role || "user"}</p>
              </div>
            </div>

            <div className="flex items-center gap-4 p-4 bg-white/5 rounded-lg">
              <div className="w-5 h-5 flex items-center justify-center">
                <span className="text-pink-400 text-lg">📅</span>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Member Since</p>
                <p className="text-lg">{user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : "Unknown"}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}